import { useMutation } from '@apollo/client';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Button, CircularProgress, DialogActions, DialogContent, Tooltip, Typography } from '@mui/material';
import { amber } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import TextContentArea from '@/components/containers/text-content-area';
import AndonCallDialogHeader from '@/components/dialogs/andon/header';
import { Context } from '@/contexts/company';
import { andonCallReopen } from '@/graphql/mutations';
import { buildSummary } from '@/helpers/andon';
import { useUtils } from '@/hocs';

const useStyles = makeStyles()((theme) => ({
  iconSizeLeft: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginRight: theme.spacing(),
  },
  iconSizeRight: {
    color: 'inherit',
    height: '1em !important',
    width: '1em !important',
    fontSize: theme.typography.pxToRem(24),
    marginLeft: theme.spacing(),
  },
  buttonWithIconRight: {
    '& > span > svg': {
      marginLeft: theme.spacing(),
    },
  },
  buttonWithIconLeft: {
    '& > span > svg': {
      marginRight: theme.spacing(),
    },
  },
  hideWhileSmall: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  buttonNeutral: {
    backgroundColor: amber[600],
    color: theme.palette.getContrastText(amber[600]),
    '&:hover': {
      backgroundColor: amber[600],
    },
  },
}));

export interface Properties {
  frozen: boolean;
  call: Schema.AndonListCallsQuery['andon'][0]['calls'][0];
  onClose(..._: unknown[]): void;
}

const AndonResolveCallDialog: FunctionComponent<Properties> = ({ call, onClose, frozen }) => {
  const [t] = useTranslation();
  const { classes, cx } = useStyles();
  const utils = useUtils();

  const [reopen, { loading: opening }] = useMutation<
    Schema.AndonCallReopenMutation,
    Schema.AndonCallReopenMutationVariables
  >(andonCallReopen);

  const { companyId } = useContext(Context);

  const callAction = call.action.resolve;
  if (!callAction || !companyId) {
    return null;
  }

  const summary = buildSummary(call.action, utils);

  return (
    <AndonCallDialogHeader onClose={onClose}>
      <DialogContent>
        {callAction.author ? (
          <TextContentArea>
            <Typography variant="subtitle2">{t(['andon:call:resolvedBy'], { defaultValue: 'Resolved by' })}</Typography>
            <Typography>{callAction.author}</Typography>
          </TextContentArea>
        ) : null}
        <TextContentArea>
          <Typography variant="subtitle2">{t(['shared:summary'], { defaultValue: 'Summary' })}</Typography>
          <Typography>{summary}</Typography>
        </TextContentArea>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" className={classes.hideWhileSmall}>
          {t(['shared:close'], { defaultValue: 'Close' })}
        </Button>
        <Tooltip
          placement={'top'}
          title={
            frozen
              ? t(['andon:workOrder:pending'], {
                  defaultValue: "A work order has been initiated for this call and it's in a pending state",
                })
              : ''
          }
        >
          <span>
            <Button
              onClick={async () =>
                reopen({
                  variables: {
                    companyId,
                    id: call.id,
                    resolutionTime: callAction.time,
                  },
                })
              }
              disabled={frozen}
              color="primary"
              variant="contained"
              className={cx(classes.buttonWithIconLeft, classes.buttonNeutral)}
            >
              {opening ? <CircularProgress className={classes.iconSizeLeft} /> : <RefreshIcon />}
              {t(['shared:reopen'], { defaultValue: 'Re-open' })}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </AndonCallDialogHeader>
  );
};

export default AndonResolveCallDialog;
